import { setup } from 'axios-cache-adapter';
import getEnv from '@/utils/env';
import queryErrorHandler from '@/utils/errorHandler';
import store from '../store';

class TsstAdminUtils {
  // Create `axios` instance with pre-configured `axios-cache-adapter` attached to it
  constructor() {
    this.instance = null;
    this.instanceWithoutHeader = null;
    this.instanceUpload = null;
  }

  // ----------------------------------- AUTH
  async preQuery() {
    const headers = {
      'Access-Control-Allow-Origin': '*',
      'Content-Type': 'application/json',
    };
    if (store.getters.idToken && store.getters.idToken !== '') {
      if (
        !store.getters.idTokenExpirationDate ||
        store.getters.idTokenExpirationDate < Date.now() / 1000
      ) {
        store.dispatch('tryAutoSignIn');
        while (store.getters.tryAutoSignInFinished === false) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }
    } else {
      store.dispatch('tryAutoSignIn');
      while (store.getters.tryAutoSignInFinished === false) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    }

    if (store.getters.idToken && store.getters.idToken !== '') {
      headers.Authorization = `Bearer ${store.getters.idToken}`;
    }

    this.instance = () =>
      setup({
        // `axios` options
        baseURL: getEnv('VUE_APP_TSST_ADMIN_API_URL'),
        timeout: 30000,
        headers,
        // `axios-cache-adapter` options
        cache: {
          maxAge: 15 * 60 * 1000,
        },
      });

    this.instanceWithoutHeader = () =>
      setup({
        // `axios` options
        baseURL: getEnv('VUE_APP_TSST_ADMIN_API_URL'),
        timeout: 30000,
        // `axios-cache-adapter` options
        cache: {
          maxAge: 15 * 60 * 1000,
        },
      });
  }

  // ------------------------------------------- API Routes

  async getAllGetRoutes() {
    await this.preQuery();
    let route = '/route/get';
    route += `?t=${new Date().toISOString()}`;
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getGetRoute(encodedUrl) {
    await this.preQuery();
    let route = `/route/get/${encodedUrl}`;
    route += `?t=${new Date().toISOString()}`;
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getAllActionRoutes() {
    await this.preQuery();
    let route = '/route/action';
    route += `?t=${new Date().toISOString()}`;
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getActionRoute(encodedUrl) {
    await this.preQuery();
    let route = `/route/action/${encodedUrl}`;
    route += `?t=${new Date().toISOString()}`;
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getEntitiesProperties() {
    await this.preQuery();
    let route = '/route/entity/properties';
    route += `?t=${new Date().toISOString()}`;
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getEntityPropertiesByEntityName(entityName) {
    await this.preQuery();
    let route = `/route/entity/${entityName}/properties`;
    route += `?t=${new Date().toISOString()}`;
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getEntityStructures() {
    await this.preQuery();
    let route = '/route/entity/structure';
    route += `?t=${new Date().toISOString()}`;
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getEntityStructureByEntityName(entityName) {
    await this.preQuery();
    let route = `/route/entity/${entityName}/structure`;
    route += `?t=${new Date().toISOString()}`;
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getQueryByRouteUrl(routeUrl, queryParameters = '') {
    await this.preQuery();
    return this.instance()
      .get(`${routeUrl}?${queryParameters || ''}`)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async postQueryByRouteUrl(routeUrl, requestBody = {}, queryParameters = '') {
    await this.preQuery();
    return this.instance()
      .post(`${routeUrl}?${queryParameters || ''}`, requestBody)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async putQueryByRouteUrl(routeUrl, requestBody = {}, queryParameters = '') {
    await this.preQuery();
    return this.instance()
      .put(`${routeUrl}?${queryParameters || ''}`, requestBody)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async deleteQueryByRouteUrl(
    routeUrl,
    requestBody = {},
    queryParameters = ''
  ) {
    await this.preQuery();
    return this.instance()
      .delete(`${routeUrl}?${queryParameters || ''}`, requestBody)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getApps(queryParameters = '', noCache = false) {
    await this.preQuery();
    let route = `/app?${queryParameters || ''}`;
    if (noCache) {
      route += `${queryParameters ? '&' : ''}t=${new Date().toISOString()}`;
    }
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getAppByAppId(appId, queryParameters = '', noCache = false) {
    await this.preQuery();
    let route = `/app/${appId}?${queryParameters || ''}`;
    if (noCache) {
      route += `${queryParameters ? '&' : ''}t=${new Date().toISOString()}`;
    }
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getAppTypes(queryParameters = '', noCache = false) {
    await this.preQuery();
    let route = `/app-type?${queryParameters || ''}`;
    if (noCache) {
      route += `${queryParameters ? '&' : ''}t=${new Date().toISOString()}`;
    }
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getAppTypeByAppTypeId(
    appTypeId,
    queryParameters = '',
    noCache = false
  ) {
    await this.preQuery();
    let route = `/app-type/${appTypeId}?${queryParameters || ''}`;
    if (noCache) {
      route += `${queryParameters ? '&' : ''}t=${new Date().toISOString()}`;
    }
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getBaseUnits(queryParameters = '', noCache = false) {
    await this.preQuery();
    let route = `/base-unit?${queryParameters || ''}`;
    if (noCache) {
      route += `${queryParameters ? '&' : ''}t=${new Date().toISOString()}`;
    }
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getH3Resolutions(queryParameters = '', noCache = false) {
    await this.preQuery();
    let route = `/h3-resolution?${queryParameters || ''}`;
    if (noCache) {
      route += `${queryParameters ? '&' : ''}t=${new Date().toISOString()}`;
    }
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getProjectTypes(queryParameters = '') {
    await this.preQuery();
    return this.instance()
      .get(`/project-type?${queryParameters || ''}`)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getMlModelAlgorithms(queryParameters = '') {
    await this.preQuery();
    return this.instance()
      .get(`/ml-model-algorithm?${queryParameters || ''}`)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getMlModelVersionEvaluationTypes(queryParameters = '') {
    await this.preQuery();
    return this.instance()
      .get(`/ml-model-version-evaluation-type?${queryParameters || ''}`)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getMlModelTypes(queryParameters = '') {
    await this.preQuery();
    return this.instance()
      .get(`/ml-model-type?${queryParameters || ''}`)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getDataTechnicalProviders(queryParameters = '', noCache = false) {
    await this.preQuery();
    let route = `/data-technical-provider?${queryParameters || ''}`;
    if (noCache) {
      route += `${queryParameters ? '&' : ''}t=${new Date().toISOString()}`;
    }
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getDataTechnicalProviderByDataTechnicalProviderId(
    dataTechnicalProviderId,
    queryParameters = '',
    noCache = false
  ) {
    await this.preQuery();
    let route = `/data-technical-provider/${dataTechnicalProviderId}?${
      queryParameters || ''
    }`;
    if (noCache) {
      route += `${queryParameters ? '&' : ''}t=${new Date().toISOString()}`;
    }
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getDataOwners(queryParameters = '', noCache = false) {
    await this.preQuery();
    let route = `/data-owner?${queryParameters || ''}`;
    if (noCache) {
      route += `${queryParameters ? '&' : ''}t=${new Date().toISOString()}`;
    }
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getDataOwnerByDataOwnerId(
    dataOwnerId,
    queryParameters = '',
    noCache = false
  ) {
    await this.preQuery();
    let route = `/data-owner/${dataOwnerId}?${queryParameters || ''}`;
    if (noCache) {
      route += `${queryParameters ? '&' : ''}t=${new Date().toISOString()}`;
    }
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getCountries(queryParameters = '', noCache = false) {
    await this.preQuery();
    let route = `/country?${queryParameters || ''}`;
    if (noCache) {
      route += `${queryParameters ? '&' : ''}t=${new Date().toISOString()}`;
    }
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getCountryByCountryId(
    countryId,
    queryParameters = '',
    noCache = false
  ) {
    await this.preQuery();
    let route = `/country/${countryId}?${queryParameters || ''}`;
    if (noCache) {
      route += `${queryParameters ? '&' : ''}t=${new Date().toISOString()}`;
    }
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getNotebooks(queryParameters = '', noCache = false) {
    await this.preQuery();
    let route = `/notebook?${queryParameters || ''}`;
    if (noCache) {
      route += `${queryParameters ? '&' : ''}t=${new Date().toISOString()}`;
    }
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getNotebookByNotebookId(
    notebookId,
    queryParameters = '',
    noCache = false
  ) {
    await this.preQuery();
    let route = `/notebook/${notebookId}?${queryParameters || ''}`;
    if (noCache) {
      route += `${queryParameters ? '&' : ''}t=${new Date().toISOString()}`;
    }
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getMappingFunctions(queryParameters = '', noCache = false) {
    await this.preQuery();
    let route = `/mapping-function?${queryParameters || ''}`;
    if (noCache) {
      route += `${queryParameters ? '&' : ''}t=${new Date().toISOString()}`;
    }
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getMdEntities(queryParameters = '', noCache = false) {
    await this.preQuery();
    let route = `/md-entity?${queryParameters || ''}`;
    if (noCache) {
      route += `${queryParameters ? '&' : ''}t=${new Date().toISOString()}`;
    }
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getMdFieldTypes(queryParameters = '', noCache = false) {
    await this.preQuery();
    let route = `/md-field-type?${queryParameters || ''}`;
    if (noCache) {
      route += `${queryParameters ? '&' : ''}t=${new Date().toISOString()}`;
    }
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getMdFormats(queryParameters = '', noCache = false) {
    await this.preQuery();
    let route = `/md-format?${queryParameters || ''}`;
    if (noCache) {
      route += `${queryParameters ? '&' : ''}t=${new Date().toISOString()}`;
    }
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getMdPeriods(queryParameters = '', noCache = false) {
    await this.preQuery();
    let route = `/md-period?${queryParameters || ''}`;
    if (noCache) {
      route += `${queryParameters ? '&' : ''}t=${new Date().toISOString()}`;
    }
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getOperators(queryParameters = '', noCache = false) {
    await this.preQuery();
    let route = `/operator?${queryParameters || ''}`;
    if (noCache) {
      route += `${queryParameters ? '&' : ''}t=${new Date().toISOString()}`;
    }
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getOperatorTypes(queryParameters = '', noCache = false) {
    await this.preQuery();
    let route = `/operator-type?${queryParameters || ''}`;
    if (noCache) {
      route += `${queryParameters ? '&' : ''}t=${new Date().toISOString()}`;
    }
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getPipelineTemplates(queryParameters = '', noCache = false) {
    await this.preQuery();
    let route = `/pipeline-template?${queryParameters || ''}`;
    if (noCache) {
      route += `${queryParameters ? '&' : ''}t=${new Date().toISOString()}`;
    }
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getPipelineTemplateByPipelineTemplateId(
    pipelineTemplateId,
    queryParameters = '',
    noCache = false
  ) {
    await this.preQuery();
    let route = `/pipeline-template/${pipelineTemplateId}?${
      queryParameters || ''
    }`;
    if (noCache) {
      route += `${queryParameters ? '&' : ''}t=${new Date().toISOString()}`;
    }
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getRoles(queryParameters = '', noCache = false) {
    await this.preQuery();
    let route = `/role?${queryParameters || ''}`;
    if (noCache) {
      route += `${queryParameters ? '&' : ''}t=${new Date().toISOString()}`;
    }
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getRoleByRoleId(roleId, queryParameters = '', noCache = false) {
    await this.preQuery();
    let route = `/role/${roleId}?${queryParameters || ''}`;
    if (noCache) {
      route += `${queryParameters ? '&' : ''}t=${new Date().toISOString()}`;
    }
    return this.instance()
      .get(route)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }

  async getUserByUserId(userId) {
    await this.preQuery();
    return this.instance()
      .get(`/user/${userId}`)
      .then((response) => response.data)
      .catch((e) => queryErrorHandler(e));
  }
}

export default TsstAdminUtils;
